import { AudienceIntelligence, Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Protein Pantry",
  industry: ["Food & Beverage", "Health Foods", "Plant-Based Protein Foods"],
  sub_industry: ["Ready-to-Cook High-Protein Vegetarian Foods", "Soya-based products: chaap, tikkis, cutlets"],
  founded_year: 2025,
  founded_original_name: "Protein Pantry",
  headquarters: ["New Delhi, India"],
  tagline_primary: "India's first high-protein, low-calorie food brand",
  mission: '"To make protein delicious and easy to cook with real ingredients for everyone."',
  vision: '"For households to reach for Protein Pantry for guilt-free indulgence and sustainable eating."',
  overview:
    "Protein Pantry is India's first high-protein, low-calorie food brand offering ready-to-cook soya chaap, tikkis, and cutlets with real ingredients and no preservatives. It targets busy, health-conscious Indians in the plant-based protein market.",
  scale: {
    revenue: 0,
    employees_estimate: 10,
    markets_served_count: 1,
    operating_countries_count: 1,
  },
  global_presence: {
    regions: [
      {
        region: "India",
        headquarters: "New Delhi, India",
        active_markets: ["India"],
        market_type: "Direct-to-consumer (online e-commerce via proteinpantry.in)",
      },
    ],
    key_markets_by_revenue_share: [],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "Pantry Green",
          hex: "#2D5F3F",
        },
        {
          name: "Cream/Off-White",
          hex: "#F5F3ED",
        },
        {
          name: "Charcoal Black",
          hex: "#1A1A1A",
        },
      ],
      secondary: [
        {
          name: "Warm Beige",
          hex: "#E8DCC8",
        },
        {
          name: "Soft Green",
          hex: "#7A9B7E",
        },
      ],
      accent: [
        {
          name: "Action Orange",
          hex: "#FF6B35",
        },
        {
          name: "Alert Red",
          hex: "#D94A3D",
        },
      ],
    },
    typography: [
      {
        font_family: "Sans-serif",
        weight: "Bold (700), Semi-Bold (600)",
        usage: "Headlines, product names, navigation",
      },
      {
        font_family: "Sans-serif",
        weight: "Regular (400), Medium (500)",
        usage: "Body text, product descriptions, FAQ content",
      },
      {
        font_family: "Sans-serif",
        weight: "Regular (400)",
        usage: "Nutritional information, pricing, small text",
      },
    ],
    logos: [
      {
        variant: "Primary",
        url: "https://proteinpantry.in/cdn/shop/files/Protein_Pantry_Logo-01.svg?v=1760806701&width=800",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Approachable", "Honest/Transparent", "Inclusive", "Relatable", "Empowering", "Warm/Friendly", "Health-Conscious but Not Preachy"],
    taglines: [
      "India's first high-protein, low-calorie food brand",
      "100% Clean, High Protein Foods, carefully crafted for busy Indians who love good food ❤️❤️",
      "We make eating healthy feel like home, not homework",
      "make protein delicious to eat and easy to cook",
    ],
    messaging_dos: [],
    messaging_donts: [],
    banned_words: [],
  },
  creative_assets: ["https://assets.zocket.com/uploads/602965ca-2df0-460c-8b08-0d4046a66b9b-Screenshot_2026-02-11_at_4.52.32_PM.png"],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  brand_architecture: {
    available_brands: ["Protein Pantry"],
    selected_brand: "Protein Pantry",
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "94%",
      patterns_learned: 342,
      avg_generation: "1.5s",
    },
    style_drift_alerts: [
      {
        title: "Protein Pantry Product Packaging",
        description: "Product packaging inconsistent with brand guidelines",
        severity: "medium",
      },
      {
        title: "Protein Pantry Logo",
        description: "Logo placement inconsistent with brand guidelines",
        severity: "medium",
      },
    ],
    preference_learning: [
      { preference: "No Preservatives", score: 92, change: 5 },
      { preference: "Convenient & Easy to Cook", score: 88, change: 4 },
      { preference: "Affordable & Value for Money", score: 86, change: 3 },
      { preference: "Healthy & Balanced", score: 82, change: 6 },
    ],
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 7.2,
    sentiment_graph: [
      {
        metric: "Customer Sentiment Score",
        value: "7.2/10",
        delta: "+0.5",
      },
      {
        metric: "Net Promoter Score (NPS)",
        value: "~35-40",
        delta: "N/A",
      },
      {
        metric: "Plant-Based RTC Market Position",
        value: "<1%",
        delta: "New entrant",
      },
      {
        metric: "Share of Voice (SOV)",
        value: "<0.5%",
        delta: "Growing",
      },
      {
        metric: "Product Rating (Aggregated)",
        value: "4.2/5",
        delta: "N/A",
      },
      {
        metric: "Repeat Purchase Intent",
        value: "~65-70%",
        delta: "Estimated",
      },
      {
        metric: "Market Growth Rate (Category)",
        value: "14.36% CAGR",
        delta: "Industry",
      },
      {
        metric: "Ready-to-Cook Market Growth",
        value: "16.4% CAGR",
        delta: "Industry",
      },
    ],
  },
  sentiment_summary: {
    time_range: ["2024", "2025"],
    themes: {
      positive: [
        "Clean Label & Transparency",
        "High Protein-to-Calorie Ratio",
        "Convenience Factor",
        "Authentic Taste",
        "Vegetarian/Vegan Friendly",
        "Guilt-Free Indulgence",
        "Innovation in Traditional Foods",
      ],
      neutral: ["Price Positioning", "Availability & Distribution", "Product Variety", "Brand Awareness", "Shelf Life Expectations"],
      negative: [
        "Texture Concerns",
        "Wheat/Gluten Content",
        "Protein Content Skepticism",
        "Delivery & Freshness",
        "Taste Inconsistency",
        "Limited Mainstream Appeal",
        "Competition from Traditional Cooking",
      ],
    },
  },
  distribution: [
    {
      brand: "GoodDot",
      share: 19,
    },
    {
      brand: "Vezlay Foods",
      share: 13.5,
    },
    {
      brand: "Blue Tribe Foods",
      share: 9,
    },
    {
      brand: "Wakao Foods",
      share: 7,
    },
    {
      brand: "Others",
      share: 51.5,
    },
  ],
  sentiment_by_product: [
    {
      product: "Tandoori Chaap",
      sentiment: 8.2,
      change_delta: 0,
    },
    {
      product: "Korean BBQ Chaap",
      sentiment: 7.8,
      change_delta: 0,
    },
    {
      product: "Sweet Potato Tikki",
      sentiment: 7.5,
      change_delta: 0,
    },
    {
      product: "Chickpea Falafel",
      sentiment: 7.9,
      change_delta: 0,
    },
    {
      product: "Beetroot Kebab",
      sentiment: 7.4,
      change_delta: 0,
    },
    {
      product: "Cheese Mushroom Cutlet",
      sentiment: 8,
      change_delta: 0,
    },
  ],
  top_topics: {
    praise: [
      {
        topic: "High Protein Content",
        weight: 10,
      },
      {
        topic: "Clean Label/No Preservatives",
        weight: 9,
      },
      {
        topic: "Taste & Flavor Quality",
        weight: 9,
      },
      {
        topic: "Convenience/Quick Preparation",
        weight: 9,
      },
      {
        topic: "Low Calorie for High Protein",
        weight: 8,
      },
      {
        topic: "Vegetarian/Vegan Options",
        weight: 8,
      },
      {
        topic: "Texture & Mouthfeel",
        weight: 7,
      },
    ],
    complaints: [
      {
        topic: "Price Sensitivity",
        weight: 10,
      },
      {
        topic: "Limited Availability",
        weight: 10,
      },
      {
        topic: "Wheat/Gluten Content",
        weight: 8,
      },
      {
        topic: "Actual Protein Content Skepticism",
        weight: 7,
      },
      {
        topic: "Texture Not Exactly Like Meat",
        weight: 7,
      },
      {
        topic: "Preparation Instructions",
        weight: 7,
      },
      {
        topic: "Limited Product Range",
        weight: 7,
      },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    {
      name: "Vezlay Foods Pvt. Ltd.",
      threat_level: "high",
      market_share: 10,
      sentiment: 72,
    },
    {
      name: "GoodDot Enterprises Pvt. Ltd.",
      threat_level: "high",
      market_share: 16,
      sentiment: 78,
    },
    {
      name: "Blue Tribe Foods Pvt. Ltd.",
      threat_level: "medium-high",
      market_share: 6,
      sentiment: 75,
    },
    {
      name: "Wakao Foods Pvt. Ltd.",
      threat_level: "medium",
      market_share: 5,
      sentiment: 70,
    },
    {
      name: "Imagine Meats (Imagine Foods Pvt. Ltd.)",
      threat_level: "medium-high",
      market_share: 7,
      sentiment: 73,
    },
    {
      name: "Sagar Trading Company (Sagar Soya Chaap)",
      threat_level: "medium",
      market_share: 4,
      sentiment: 65,
    },
    {
      name: "Gabru Di Chaap",
      threat_level: "low-medium",
      market_share: 3,
      sentiment: 68,
    },
    {
      name: "Veggie Champ",
      threat_level: "low-medium",
      market_share: 3,
      sentiment: 66,
    },
  ],
  secondary_competitors: [
    {
      name: "MTR Foods (Ready-to-Eat Meals)",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "ITC Kitchen of India / ITC MasterChef Smart Protein",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Tata Sampann (Yumside RTE Range)",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "iD Fresh Food",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Haldiram's (RTE Range)",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Plant-Based Protein Powder Brands (OZiva, MuscleBlaze Plant, Wellbeing Nutrition)",
      threat_level: "low",
      market_share: 0,
      sentiment: 0,
    },
  ],
  share_of_voice: [
    {
      channel: "Social Media",
      distribution: {
        GoodDot: 22,
        "Blue Tribe Foods": 18,
        "Imagine Meats": 15,
        "Vezlay Foods": 12,
        "Wakao Foods": 10,
        "Protein Pantry": 4,
        Others: 19,
      },
    },
    {
      channel: "Digital/Search",
      distribution: {
        GoodDot: 20,
        "Blue Tribe Foods": 14,
        "Imagine Meats": 12,
        "Vezlay Foods": 16,
        "Wakao Foods": 10,
        "Protein Pantry": 5,
        Others: 23,
      },
    },
    {
      channel: "Retail/Modern Trade",
      distribution: {
        GoodDot: 16,
        "Blue Tribe Foods": 12,
        "Imagine Meats": 14,
        "Vezlay Foods": 18,
        "Wakao Foods": 11,
        "Protein Pantry": 3,
        Others: 26,
      },
    },
    {
      channel: "PR/Media Coverage",
      distribution: {
        GoodDot: 25,
        "Blue Tribe Foods": 20,
        "Imagine Meats": 15,
        "Vezlay Foods": 8,
        "Wakao Foods": 12,
        "Protein Pantry": 3,
        Others: 17,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Brand Awareness Score 2025",
      brands: [
        {
          brand_name: "GoodDot",
          score: 65,
        },
        {
          brand_name: "Vezlay Foods",
          score: 58,
        },
        {
          brand_name: "Imagine Meats",
          score: 52,
        },
        {
          brand_name: "Blue Tribe Foods",
          score: 48,
        },
        {
          brand_name: "Wakao Foods",
          score: 42,
        },
        {
          brand_name: "Protein Pantry",
          score: 15,
        },
      ],
    },
    {
      metric_name: "Customer Satisfaction Index 2025",
      brands: [
        {
          brand_name: "GoodDot",
          score: 78,
        },
        {
          brand_name: "Blue Tribe Foods",
          score: 75,
        },
        {
          brand_name: "Imagine Meats",
          score: 73,
        },
        {
          brand_name: "Vezlay Foods",
          score: 72,
        },
        {
          brand_name: "Wakao Foods",
          score: 70,
        },
        {
          brand_name: "Protein Pantry",
          score: 0,
        },
      ],
    },
    {
      metric_name: "Net Promoter Score 2025",
      brands: [
        {
          brand_name: "GoodDot",
          score: 60,
        },
        {
          brand_name: "Blue Tribe Foods",
          score: 58,
        },
        {
          brand_name: "Imagine Meats",
          score: 56,
        },
        {
          brand_name: "Vezlay Foods",
          score: 55,
        },
        {
          brand_name: "Wakao Foods",
          score: 54,
        },
        {
          brand_name: "Protein Pantry",
          score: 0,
        },
      ],
    },
    {
      metric_name: "Brand Trust Quotient 2025",
      brands: [
        {
          brand_name: "GoodDot",
          score: 72,
        },
        {
          brand_name: "Vezlay Foods",
          score: 68,
        },
        {
          brand_name: "Imagine Meats",
          score: 65,
        },
        {
          brand_name: "Blue Tribe Foods",
          score: 64,
        },
        {
          brand_name: "Wakao Foods",
          score: 66,
        },
        {
          brand_name: "Protein Pantry",
          score: 0,
        },
      ],
    },
    {
      metric_name: "Product Quality Score 2025",
      brands: [
        {
          brand_name: "GoodDot",
          score: 82,
        },
        {
          brand_name: "Blue Tribe Foods",
          score: 80,
        },
        {
          brand_name: "Vezlay Foods",
          score: 78,
        },
        {
          brand_name: "Imagine Meats",
          score: 77,
        },
        {
          brand_name: "Wakao Foods",
          score: 76,
        },
        {
          brand_name: "Protein Pantry",
          score: 0,
        },
      ],
    },
    {
      metric_name: "Purchase Intent Index 2025",
      brands: [
        {
          brand_name: "GoodDot",
          score: 70,
        },
        {
          brand_name: "Vezlay Foods",
          score: 68,
        },
        {
          brand_name: "Blue Tribe Foods",
          score: 65,
        },
        {
          brand_name: "Imagine Meats",
          score: 64,
        },
        {
          brand_name: "Wakao Foods",
          score: 62,
        },
        {
          brand_name: "Protein Pantry",
          score: 0,
        },
      ],
    },
  ],
  positioning: {
    competitive_advantages: [
      "Clean label focus: No maida, no preservatives, no oil, no sugar",
      "Targeting everyday consumers rather than just fitness enthusiasts",
      "Authentic founder story: Ex-chef and fitness junkie",
    ],
    improvement_areas: [
      "Limited distribution compared to competitors",
      "Low brand awareness",
      "Weak social media presence",
      "Lack of funding and scale compared to competitors",
      "Narrow product range",
    ],
    disruption_watchlist: ["Mister Veg", "Shaka Harry Foods Pvt. Ltd.", "Bveg Foods", "Vegolution (Hello Tempayy)", "Prot (Prot Block)"],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "Protein Pantry targets urban millennials in India, focusing on health-conscious, convenience-seeking consumers who prioritize quality and sustainability. The core audience is aged 25-44, with a significant presence in urban areas and a strong inclination towards digital platforms for brand discovery and purchase.",
  core_demographics: {
    primary_age_range: "25-44",
    gender_split: {
      male: 56.5,
      female: 43.5,
    },
    demographics_distribution: {
      age: [
        { age_range: "25-34", percentage: 30 },
        { age_range: "35-44", percentage: 40 },
        { age_range: "45-54", percentage: 20 },
      ],
      education: [
        { education_level: "Bachelor's+", percentage: 30 },
        { education_level: "Some College", percentage: 40 },
        { education_level: "Graduate Degree", percentage: 20 },
        { education_level: "High School", percentage: 10 },
      ],
      geographic: [
        { location: "Urban", percentage: 50 },
        { location: "Suburban", percentage: 30 },
        { location: "Rural", percentage: 20 },
      ],
    },
    income_median_usd: 18000,
    location_type: "Urban",
  },
  occupation_distribution: [
    { occupation: "Working Professionals", percentage: 20 },
    { occupation: "Students", percentage: 40 },
    { occupation: "Gym Trainers", percentage: 30 },
    { occupation: "Entrepreneurs", percentage: 10 },
  ],
  interests: {
    top_categories: [
      {
        category: "Health & Wellness",
        score: 9,
      },
      {
        category: "Fitness & Exercise",
        score: 8,
      },
      {
        category: "Nutrition & Diet",
        score: 9,
      },
      {
        category: "Cooking & Recipes",
        score: 7,
      },
      {
        category: "Technology & Apps",
        score: 8,
      },
      {
        category: "Sustainable Living",
        score: 7,
      },
      {
        category: "Work-Life Balance",
        score: 8,
      },
      {
        category: "Food Innovation",
        score: 8,
      },
      {
        category: "Personal Development",
        score: 7,
      },
      {
        category: "Social Media & Content",
        score: 8,
      },
    ],
    brand_affinities: [
      {
        brand: "MuscleBlaze",
        affinity: 32,
      },
      {
        brand: "Amul",
        affinity: 28,
      },
      {
        brand: "OZiva",
        affinity: 26,
      },
      {
        brand: "Swiggy/Zomato",
        affinity: 45,
      },
      {
        brand: "BigBasket/Amazon Fresh",
        affinity: 38,
      },
      {
        brand: "Cult.fit",
        affinity: 24,
      },
      {
        brand: "HealthifyMe",
        affinity: 22,
      },
      {
        brand: "Blue Tribe Foods",
        affinity: 18,
      },
      {
        brand: "GoodDot",
        affinity: 16,
      },
      {
        brand: "Nykaa (wellness products)",
        affinity: 20,
      },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        online_direct: 36.6,
        e_commerce_marketplaces: 41.5,
        quick_commerce: 13.2,
        modern_retail: 6.3,
        specialty_health_stores: 2.4,
      },
      purchase_frequency: "1.8-2.2 times per month",
      avg_order_value_usd: 12.5,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Problem Recognition",
          description: "Realizes need for convenient, healthy, high-protein meals; discovers through social media, health influencers, or search",
          duration_days: 5,
        },
        {
          stage: 2,
          name: "Information Gathering",
          description: "Reads product reviews, checks ingredients, compares nutritional values, watches recipe videos, seeks peer recommendations",
          duration_days: 7,
        },
        {
          stage: 3,
          name: "Evaluation & Comparison",
          description: "Compares with alternatives (meal prep, other brands, traditional protein sources), evaluates price-value proposition",
          duration_days: 4,
        },
        {
          stage: 4,
          name: "First Order",
          description: "Places trial order (often smaller quantity), uses discount/offer if available, prefers mobile app checkout with UPI/digital payment",
          duration_days: 1,
        },
        {
          stage: 5,
          name: "Evaluation & Loyalty",
          description: "Tests product quality, taste, convenience; shares experience on social media; decides on repeat purchase or subscription",
          duration_days: 10,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        mobile_smartphone: 78.5,
        desktop_laptop: 20,
        tablet: 1.5,
      },
      media_channels: {
        instagram: 31.9,
        youtube: 27.6,
        whatsapp: 15.7,
        facebook: 10.5,
        google_search: 8.6,
        linkedin: 3.3,
        twitter_x: 2.4,
      },
    },
  },
  platforms: {
    distribution: {
      instagram: 31.9,
      youtube: 27.6,
      whatsapp: 15.7,
      facebook: 10.5,
      google_search: 8.6,
      linkedin: 3.3,
      twitter_x: 2.4,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    {
      platform: "Instagram",
      followers: 2010,
      health_score: 40,
    },
    {
      platform: "Facebook",
      followers: 1500,
      health_score: 30,
    },
    {
      platform: "LinkedIn",
      followers: 183,
      health_score: 10,
    },
    {
      platform: "YouTube",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "TikTok",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Twitter/X",
      followers: 50,
      health_score: 10,
    },
    {
      platform: "Pinterest",
      followers: 0,
      health_score: 0,
    },
  ],
  sentiment_distribution: [
    {
      label: "Positive",
      percentage: 70,
    },
    {
      label: "Neutral",
      percentage: 20,
    },
    {
      label: "Negative",
      percentage: 10,
    },
  ],
  recent_comments: [
    {
      user: "health_enthusiast",
      platform: "Instagram",
      timeAgo: "2 days ago",
      status: "positive",
      commentText: "Love the new products!",
      postContext: "Before & After post",
    },
    {
      user: "foodie_lover",
      platform: "Instagram",
      timeAgo: "1 week ago",
      status: "neutral",
      commentText: "Love the new products!",
      postContext: "Corporate communication post",
    },
  ],
  engagement: {
    engagement_by_platform: [
      {
        platform: "Instagram",
        engagement_rate: 2.2,
      },
      {
        platform: "Facebook",
        engagement_rate: 0,
      },
      {
        platform: "YouTube",
        engagement_rate: 0,
      },
      {
        platform: "LinkedIn",
        engagement_rate: 0,
      },
      {
        platform: "Website/E-commerce",
        engagement_rate: 0,
      },
    ],
    best_performing_content_types: [
      {
        content_type: "Recipe Reels",
        performance: 9,
      },
      {
        content_type: "Product Photography/Carousel Posts",
        performance: 6,
      },
      {
        content_type: "Educational Content",
        performance: 7,
      },
      {
        content_type: "User-Generated Content",
        performance: 8,
      },
      {
        content_type: "Stories",
        performance: 8,
      },
      {
        content_type: "Influencer Collaborations",
        performance: 9,
      },
      {
        content_type: "Meal Prep/Convenience Content",
        performance: 8,
      },
    ],
    follower_growth_trend: [
      {
        month: "September",
        followers: 750,
      },
      {
        month: "October",
        followers: 1023,
      },
      {
        month: "November",
        followers: 1692,
      },
      {
        month: "December",
        followers: 1643,
      },
      {
        month: "January",
        followers: 1702,
      },
      {
        month: "February",
        followers: 2010,
      },
    ],
  },
};

export const mBrandIntelligenceProteinPantry: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
